import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Target, Heart, Lightbulb } from "lucide-react";

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description: "To empower developers and tech enthusiasts with high-quality, accessible content that helps them stay ahead in the ever-evolving tech landscape.",
    },
    {
      icon: Heart,
      title: "Community First",
      description: "We believe in building a supportive community where knowledge is shared freely and everyone can learn and grow together.",
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "We're passionate about exploring new technologies and sharing insights on the latest trends shaping the future of development.",
    },
    {
      icon: Users,
      title: "Collaboration",
      description: "We work with industry experts and thought leaders to bring you diverse perspectives and in-depth technical knowledge.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="border-b border-border bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
              About TechBlog
            </h1>
            <p className="text-lg text-muted-foreground md:text-xl">
              We're a team of passionate developers, designers, and tech enthusiasts dedicated to sharing knowledge and inspiring innovation in the tech community.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-6 text-3xl font-bold">Our Story</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              TechBlog was founded in 2024 with a simple vision: to create a platform where developers of all skill levels could find high-quality, practical content about modern web development and technology.
            </p>
            <p>
              What started as a small blog has grown into a thriving community of thousands of readers who rely on us for the latest insights, tutorials, and industry trends. Our content covers everything from beginner-friendly tutorials to advanced architectural patterns, ensuring there's something valuable for everyone.
            </p>
            <p>
              Today, we're proud to be a trusted resource for developers worldwide, helping them build better applications, advance their careers, and stay current with the rapidly evolving tech landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="border-y border-border bg-muted/50">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">What We Stand For</h2>
            <p className="text-muted-foreground">
              The core values that guide everything we do
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {values.map((value, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                    <value.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>{value.title}</CardTitle>
                  <CardDescription className="text-base">
                    {value.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold">Meet Our Team</h2>
          <p className="text-muted-foreground">
            The people behind the content you love
          </p>
        </div>

        <div className="mx-auto max-w-3xl">
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">
                Our team consists of experienced developers, technical writers, and industry veterans who are passionate about sharing their knowledge. With backgrounds spanning frontend development, system architecture, UI/UX design, and DevOps, we bring diverse perspectives to every article we publish.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
